<!-- =========================================================================================
  File Name: CrewDataManagement.vue
  Description: Crew Data Management Top Page
  Component Name: CrewDataManagement
========================================================================================== -->

<template>
  <vx-card id="crew-data-management-card">
    <!-- ローディング中 -->
    <div
      v-if="isLoading"
      id="loading-screen"
      class="flex items-center justify-center"
    >
      <div class="text-center">
        <p class="aisea-text-20-400-black mb-4">
          画面の構成を取得しています...
        </p>
      </div>
    </div>

    <!-- 構成取得成功時 -->
    <div v-else-if="functionScheme" id="crew-data-management" class="h-full">
      <dynamic-table
        :functions="functionScheme.functions"
        @executeProcess="onExecuteProcess"
      />
    </div>

    <!-- 構成取得失敗時 -->
    <div
      v-else
      id="error-screen"
      class="flex items-center justify-center gap-2"
    >
      <div class="text-center">
        <feather-icon
          icon="AlertTriangleIcon"
          svgClasses="h-12 w-12"
          class="aisea-text-14-400-red"
        />
        <div class="aisea-text-20-400-black mb-4">
          画面の構成取得に失敗しました。
        </div>
        <div class="aisea-text-16-400-dark-gray mb-4 flex">
          画面構成情報が正しく設定されているか、Aideaまでお問い合わせください。
        </div>
        <div>
          <button-component
            icon="RotateCwIcon"
            label="再読み込み"
            @click="reloadPage"
          ></button-component>
        </div>
      </div>
    </div>
    <vs-prompt
      title="データ入出力に失敗しました"
      type="alert"
      :color="AISEA_COLOR.RED"
      accept-text="OK"
      :active.sync="showErrorPrompt"
      class="crew-data-management-error-prompt"
    >
      <div
        class="pb-2"
        v-for="errorMessage in errorMessages"
        :key="errorMessage"
      >
        {{ errorMessage }}
      </div>
    </vs-prompt>
  </vx-card>
</template>

<script>
import { ColorMixin } from "@/mixins";
import DateUtil from "@/utils/DateUtil.js";
import DownloadUtil from "@/utils/DownloadUtils.js";

// Components
import DynamicTable from "./components/DynamicTable.vue";
import ButtonComponent from "./components/ButtonComponent.vue";

// Store
import { createNamespacedHelpers } from "vuex";

const { mapActions: mapActionsOfCrewDataManagement } = createNamespacedHelpers(
  "crewDataManagement"
);

export default {
  components: {
    DynamicTable,
    ButtonComponent,
  },
  mixins: [ColorMixin],
  data() {
    return {
      isLoading: true,
      common: null,
      functionScheme: null,
      showErrorPrompt: false,
      errorMessages: [],
    };
  },
  methods: {
    ...mapActionsOfCrewDataManagement([
      "fetchManningVessels",
      "fetchLatestCutoffDate",
      "fetchDataManagementFunctionScheme",
      "executeDynamicApi",
      "fetchEnterpriseCrews",
    ]),

    reloadPage() {
      window.location.reload();
    },

    async fetchScreenData() {
      try {
        // 画面構成取得をローカルファイル読み込み用（ローカルテストで使用する時コメントイン）
        // let res = require("./SampleFunctionScheme.json");
        // this.functionScheme = res.functionScheme;

        // 画面構成取得開始
        const res = await this.fetchDataManagementFunctionScheme();
        if (res.success) {
          this.functionScheme = res.data;
        } else {
          this.functionScheme = null;
          this.$log.error(
            "Failed to fetch data management function scheme:",
            res.error || res.message
          );
        }

        // 船舶一覧を取得
        await this.fetchManningVessels();

        // 船員マスターリポジトリを取得
        await this.fetchEnterpriseCrews();

        // 月締め機能がある場合は直近の月締め日を取得;
        if (
          this.functionScheme &&
          this.functionScheme.common.hasVesselCutoffFunction
        ) {
          await this.fetchLatestCutoffDate();
        }
      } catch (error) {
        this.functionScheme = null;
        this.$log.error("Unexpected error:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async onExecuteProcess({ formData, process, components }) {
      try {
        // APIパラメータ定義を取得
        const parametersDefinition = process.api.parameters;

        // 動的にAPIパラメータを生成
        const apiParams = this.generateApiParameters(
          formData,
          parametersDefinition
        );

        // APIリクエストペイロードを構築
        const payload = {
          processType: process.processType,
          api: process.api,
          params: apiParams,
        };

        // APIを実行
        const result = await this.executeDynamicApi(payload);

        // 結果を表示（成功時）
        if (result.processType === "download") {
          if (result.success) {
            // TODO:複数行エラーメッセージが入っていたらエラーメッセージを表示
            const fileName = this.generateDynamicFileName(
              process.saveFileNameFormat,
              components,
              formData
            );

            await DownloadUtil.downloadFile(
              result.data,
              fileName,
              process.mimeType
            );

            this.$vs.notify({
              title: process.notify.successfulTitle,
              text: fileName,
              iconPack: "feather",
              icon: "icon-check",
              color: "success",
              class: "crew-data-management-notify",
            });
          } else {
            if (typeof result.userMessage === "string") {
              throw new Error(result.userMessage);
            } else if (Array.isArray(result.userMessage)) {
              // 複数行のエラーメッセージのときだけerrorPromptを表示
              this.showErrorPrompt = true;
              this.errorMessages = result.userMessage;
              return;
            }
          }
        }

        if (result.processType === "upload") {
          if (result.success) {
            this.$vs.notify({
              title: process.notify.successfulTitle,
              text: result.userMessage,
              iconPack: "feather",
              icon: "icon-check",
              color: "success",
            });
          } else {
            throw new Error(result.userMessage);
          }
        }

        if (result.processType === "vesselCutoff") {
          if (result.success) {
            this.$vs.notify({
              title: process.notify.successfulTitle,
              text: result.userMessage,
              iconPack: "feather",
              icon: "icon-check",
              color: "success",
            });
          } else {
            throw new Error(result.userMessage);
          }
        }
      } catch (error) {
        this.$log.error("Process Execution Error:", error);
        this.$vs.notify({
          title: process.notify.failureTitle,
          text: error.message,
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
          fixed: true,
        });
      }
    },

    generateApiParameters(formData, parametersDefinition) {
      const params = {};

      parametersDefinition.forEach(paramDef => {
        const {
          name,
          type,
          parameterValue,
          required,
          mappingComponentId,
          format,
          dateFormat,
        } = paramDef;

        // コンポーネントIDに基づいてformDataから値を取得
        const componentValue = formData[mappingComponentId];

        if (type === "array") {
          if (!Array.isArray(componentValue)) {
            params[name] = [componentValue[parameterValue]];
          } else {
            params[name] = componentValue.map(item => item[parameterValue]);
          }
        } else if (type === "string" && format === "date") {
          // 日付型の処理（フォーマット）
          if (componentValue && typeof componentValue === "object") {
            if (componentValue.dateStart && !componentValue.dateEnd) {
              // DatePickerのmodeがsingleの場合
              params[name] = DateUtil.formatDateWithTimezone(
                componentValue.dateStart,
                dateFormat,
                "Asia/Tokyo"
              );
            } else {
              // DatePickerのmodeがrangeの場合
              if (name === "start_date" && componentValue.dateStart) {
                params[name] = DateUtil.formatDateWithTimezone(
                  componentValue.dateStart,
                  dateFormat,
                  "Asia/Tokyo"
                );
              }
              if (name === "end_date" && componentValue.dateEnd) {
                params[name] = DateUtil.formatDateWithTimezone(
                  componentValue.dateEnd,
                  dateFormat,
                  "Asia/Tokyo"
                );
              }
            }
          }
        } else if (type === "file") {
          let fd = new FormData();
          for (let i = 0; i < componentValue.length; i++) {
            fd.append("file", componentValue[i]);
          }
          params[name] = fd;
        } else {
          // その他の型
          params[name] = componentValue || null;
        }

        // 必須パラメータのバリデーション
        if (required && (params[name] === null || params[name] === undefined)) {
          throw new Error(`${name} は必須項目です`);
        }
      });

      return params;
    },

    /**
     * 動的にファイル名を生成する
     * @param {String} saveFileNameFormat - プレースホルダー付きのファイル名フォーマット
     * @param {Array} components - ダイアログ内のコンポーネントデータ
     * @param {Object} formData - 各コンポーネントの現在の値
     * @returns {String} - 生成されたファイル名
     */
    generateDynamicFileName(saveFileNameFormat, components, formData) {
      // プレースホルダーを抽出
      const placeholders = saveFileNameFormat.match(/{\w+}/g);
      if (!placeholders) return saveFileNameFormat;

      // プレースホルダーを順番に置換
      let fileName = saveFileNameFormat;
      placeholders.forEach(placeholder => {
        const key = placeholder.replace(/[{}]/g, ""); // {key} -> key
        const component = components.find(comp => comp.id === key);

        if (!component) return; // 該当するコンポーネントがない場合はスキップ

        // コンポーネントの値を取得
        const value = this.getComponentValue(component, formData[key]);
        fileName = fileName.replace(placeholder, value || "unknown"); // プレースホルダーを値で置換
      });

      return fileName;
    },

    /**
     * コンポーネントの値をフォーマットして取得する
     * @param {Object} component - コンポーネントデータ
     * @param {*} value - 現在の値
     * @returns {String} - フォーマットされた値
     */
    getComponentValue(component, value) {
      if (!value) return null;

      switch (component.type) {
        case "DatePicker":
          if (value.dateStart && value.dateEnd) {
            // rangeモードの場合、日付範囲を結合
            return `${DateUtil.formatDateWithTimezone(
              value.dateStart,
              "YYYYMMDD",
              "Asia/Tokyo"
            )}-${DateUtil.formatDateWithTimezone(
              value.dateEnd,
              "YYYYMMDD",
              "Asia/Tokyo"
            )}`;
          } else if (value.dateStart) {
            // singleモードの場合
            return DateUtil.formatDateWithTimezone(
              value.dateStart,
              "YYYYMMDD",
              "Asia/Tokyo"
            );
          }
          return null;

        case "SelectBox":
          if (Array.isArray(value)) {
            return value.map(item => item.label).join(",");
          } else if (typeof value === "object" && value.label) {
            return value.label;
          }
          return null;

        case "InputBox":
          return value;

        case "DropZone":
          return Array.isArray(value) && value.length > 0
            ? value.map(file => file.name).join(",")
            : "no_files";

        default:
          // 未対応のtypeはそのまま返す
          return value;
      }
    },
  },

  async created() {
    await this.fetchScreenData();
  },
  async mounted() {
    //  this.$emit("checkAlert");
    const routerViewElement = document.getElementsByClassName("router-view");
    routerViewElement[0].style.padding = "2.2rem";
  },
};
</script>

<style lang="scss">
@import "src/assets/scss/aisea/variables";
.crew-data-management-error-prompt {
  .vs-dialog-text {
    scroll-behavior: auto;
    max-height: calc(100vh - 160px);
    overflow: auto;
  }
  .vs-dialog-cancel-button {
    display: none;
  }
}

#crew-data-management-card {
  .output-data-list {
    display: grid;
    grid-template-rows: repeat(auto-fill, 60px);
    grid-auto-rows: 60px;

    .row {
      display: grid;
      grid-template-columns: minmax(220px, auto) 1fr;
      justify-items: start;
      align-items: center;
      column-gap: 20px;

      padding: 0 10px 0 10px;
      border-bottom: solid 1px #e2e2e2;
    }
  }

  #loading-screen {
    height: 60vh;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #f9fafb;
    color: #495057;
    .loading-text {
      margin-bottom: 1rem;
      font-size: 1.2rem;
      color: #6c757d;
    }
  }

  #error-screen {
    height: 60vh;
    background-color: #f8f9fa;
    color: #495057;
    .btn-primary {
      padding: 0.5rem 1rem;
      background-color: #007bff;
      border: none;
      border-radius: 4px;
      color: white;
      cursor: pointer;
      &:hover {
        background-color: #0056b3;
      }
    }
  }
}
</style>